import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Copy, Eye, KeyRound } from 'lucide-react';
import { api } from '../../lib/api';
import { Button, Card, Field, Input, Pill } from '../../components/ui';
import type { MapProviderView } from './MapProviderCard';

/**
 * The Google Maps key, on the API keys page beside the gateway and storage
 * ones: every secret the platform holds is managed in one place.
 *
 * Unlike those two, this field does NOT come back pre-filled. The rest of the
 * platform's rule is that a secret is never in an API response, and this key is
 * new enough to start on the right side of it: the card shows that a key is
 * saved and its last four characters, so a refresh still tells you where you
 * stand, and seeing the whole thing is a deliberate act — your own password,
 * audited, exactly like revealing an API key.
 *
 * Which APIs to enable in Google Cloud, and what to restrict the key to, are
 * spelled out on Settings → Map provider; this card links there rather than
 * saying it twice and letting the two drift apart.
 */
export function MapProviderCredentialsCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['map-provider', 'settings'],
    queryFn: () => api.get<MapProviderView>('/map-provider/settings'),
  });

  const [value, setValue] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [asking, setAsking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put('/map-provider/settings', body),
    onSuccess: async (_r, body) => {
      setError(null);
      setValue('');
      setRevealed(null);
      setNotice((body as { clearKey?: boolean }).clearKey ? 'Key removed.' : 'Key saved.');
      setTimeout(() => setNotice(null), 4000);
      await queryClient.invalidateQueries({ queryKey: ['map-provider'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save'),
  });

  const reveal = useMutation({
    mutationFn: () => api.post<{ apiKey: string }>('/map-provider/reveal', { password }),
    onSuccess: (r) => {
      setRevealed(r.apiKey);
      setAsking(false);
      setPassword('');
      setError(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not reveal'),
  });

  const s = query.data;
  if (!s) return null;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <KeyRound className="size-4 text-muted" />
          Map provider key
        </span>
      }
      action={
        s.keyConfigured ? (
          <Button
            size="sm"
            variant="ghost"
            icon={<Eye className="size-3.5" />}
            onClick={() => {
              setAsking((v) => !v);
              setRevealed(null);
            }}
          >
            Show the key
          </Button>
        ) : null
      }
    >
      <div className="space-y-4 p-5">
        <p className="text-sm text-muted">
          The Google Maps API key. Optional — the map searches through OpenStreetMap and draws OSM and
          Esri tiles without one. Which Google Cloud APIs to enable and how to restrict this key are
          on{' '}
          <Link className="text-primary hover:underline" to="/settings">
            Settings → Map provider
          </Link>
          .
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <Pill state tone={s.keyConfigured ? 'success' : 'neutral'}>
            {s.keyConfigured ? `saved · ${s.keyHint}` : 'not configured'}
          </Pill>
          {s.keyConfigured && s.lastFailure && (
            <span className="text-xs text-warn">{s.lastFailure.message}</span>
          )}
        </div>

        <Field
          label={
            s.keyConfigured
              ? 'Replace the key — the saved one is not shown here'
              : 'Google Maps API key'
          }
        >
          <Input
            type="password"
            name="map-google-api-key"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={s.keyConfigured ? 'saved — type to replace' : 'AIza…'}
            className="font-mono"
            autoComplete="new-password"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
          />
        </Field>

        {asking && (
          <form
            className="flex flex-wrap items-end gap-3 rounded-lg border border-line bg-surface-2 px-4 py-3"
            onSubmit={(e) => {
              e.preventDefault();
              reveal.mutate();
            }}
          >
            <Field label="Your password — showing a key is re-authorised and audited">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus
                required
              />
            </Field>
            <Button type="submit" size="sm" variant="primary" loading={reveal.isPending}>
              Show it
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setAsking(false);
                setPassword('');
              }}
            >
              Cancel
            </Button>
          </form>
        )}

        {revealed && (
          <div className="flex items-center gap-2 rounded-lg border border-line bg-surface-2 px-4 py-3">
            <code className="flex-1 break-all font-mono text-sm text-ink">{revealed}</code>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              onClick={async () => {
                await navigator.clipboard.writeText(revealed);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              <Copy className="size-3" />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}

        {error && <p className="text-sm font-medium text-danger">{error}</p>}
        {notice && <p className="text-sm text-success">{notice}</p>}
      </div>

      <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3.5">
        {s.keyConfigured && (
          <Button
            variant="ghost"
            size="sm"
            loading={save.isPending}
            onClick={() => save.mutate({ clearKey: true, geocoder: 'nominatim', tiles: false })}
          >
            Remove the key
          </Button>
        )}
        <Button
          variant="primary"
          loading={save.isPending}
          disabled={value.trim() === ''}
          onClick={() => save.mutate({ apiKey: value.trim() })}
        >
          Save key
        </Button>
      </footer>
    </Card>
  );
}
